import type {
  CashFlowRow,
  DisplayCurrency,
  PeriodMeta,
} from "@/hooks/finance/cash-flow/useCashFlow";
import type { BSRow, Period } from "@/hooks/finance/balance-sheet/useBalanceSheet";

/**
 * Investor snapshot (de)serialization.
 *
 * The three finance reports compute their rows client-side and store per-period
 * figures in `Map<string, number>` (plus nested `children` maps and the
 * opening/closing `Map<string, PeriodMeta>`), which are NOT JSON-serializable.
 * These helpers convert those Maps to plain objects for storage and rehydrate
 * them on the public read side so the existing table components render unchanged.
 */

type PeriodLabel = { key: string; label: string };

export type SerializedCashFlowRow = Omit<CashFlowRow, "values" | "children"> & {
  values: Record<string, number>;
  children?: SerializedCashFlowRow[];
};

export type SerializedBSRow = Omit<BSRow, "values"> & {
  values: Record<string, number>;
};

export type SerializedReportBundle = {
  cashflow: {
    rows: SerializedCashFlowRow[];
    periodLabels: PeriodLabel[];
    periodKeys: string[];
    openingMeta: Record<string, PeriodMeta>;
    closingMeta: Record<string, PeriodMeta>;
  };
  pnl: {
    rows: SerializedCashFlowRow[];
    periodLabels: PeriodLabel[];
    periodKeys: string[];
  };
  balanceSheet: {
    rows: SerializedBSRow[];
    periods: Period[];
  };
};

export type SnapshotPayload = {
  version: 1;
  company: string;
  dateFrom: string;
  dateTo: string;
  viewPeriod: string;
  generatedAt: string;
  currencies: DisplayCurrency[];
  reports: Record<DisplayCurrency, SerializedReportBundle>;
};

// ── CashFlowRow ──────────────────────────────────────────────────────────
export function serializeCashFlowRows(
  rows: CashFlowRow[]
): SerializedCashFlowRow[] {
  return rows.map((r) => ({
    ...r,
    values: Object.fromEntries(r.values),
    children: r.children ? serializeCashFlowRows(r.children) : undefined,
  }));
}

export function deserializeCashFlowRows(
  rows: SerializedCashFlowRow[]
): CashFlowRow[] {
  return rows.map((r) => ({
    ...r,
    values: new Map(Object.entries(r.values ?? {})),
    children: r.children ? deserializeCashFlowRows(r.children) : undefined,
  }));
}

// ── BSRow ────────────────────────────────────────────────────────────────
export function serializeBSRows(rows: BSRow[]): SerializedBSRow[] {
  return rows.map((r) => ({ ...r, values: Object.fromEntries(r.values) }));
}

export function deserializeBSRows(rows: SerializedBSRow[]): BSRow[] {
  return rows.map((r) => ({
    ...r,
    values: new Map(Object.entries(r.values ?? {})),
  }));
}

// ── PeriodMeta maps (opening/closing) ───────────────────────────────────
// PeriodMeta itself is a plain object (nested arrays/records), so only the
// enclosing Map needs conversion.
export function serializeMetaMap(
  map?: Map<string, PeriodMeta>
): Record<string, PeriodMeta> {
  return map ? Object.fromEntries(map) : {};
}

export function deserializeMetaMap(
  obj?: Record<string, PeriodMeta>
): Map<string, PeriodMeta> {
  return new Map(Object.entries(obj ?? {}));
}
